import React, { useState, useEffect } from 'react';
import { Drawer, Box, IconButton, Tooltip } from '@mui/material';
import Routes from '../../routes';
import {
    LoadingBar,
} from '../Common';
import { reportOptions } from './utils';
import ReportOptions from './ReportOptions';
import ReportContainer from './ReportContainer';
import { ComplianceCheckup, ValidateLeakInspections,  } from '../../pages';
import KeyboardTabIcon from '@mui/icons-material/KeyboardTab';
import "./PowerBI.css";
import UserInfo from '../../UserInfo';

import uuid from '../../Utils/uuid';

const PowerBI = ({
    match: {
        params: {
            name: reportName = ''
        } = {}
    } = {},
    title = 'BI Dashboard',
    trakrefReports = null,
    rls = false,
    setAlert,
    closeAndResetAlert,
}) => {
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [reportsLoading, setReportsLoading] = useState(false);
    const [allReports, setAllReports] = useState([]);
    const [savedReports, setSavedReports] = useState([]);
    const [reportListExpanded, setReportListExpanded] = useState(0);
    const [selectedReport, setSelectedReport] = useState({ type: null, reportId: null, embedUrl: null, accessToken: null, name: "" });
    const [optionNavVisibile, setOptionNavVisibile] = useState(true);
    const [filterSearch, setFilterSearch] = useState("");
    const [searchIsOn, setSearchIsOn] = useState(false);
    const [breadCrumbs, setBreadCrumbs] = useState([{ text: title }]);
    const reportOptionsContainerHeight = (!trakrefReports && selectedReport.id) ? '100%' : '86vh';

    // Hide nav menu (reports / analytics options)
    // When user is Contractor Lite technician manager and not native instance
    // [isNativeServiceProvider] check if user is native contractor lite or not
    const hideReportNavMenu = UserInfo.isTechnicianManager() && !UserInfo.isNativeServiceProvider();

    const childrenStyle = {
        flexGrow: 1,
        position: 'relative',
        marginLeft: hideReportNavMenu ? 0 : (optionNavVisibile ? '15vw' : '3vw'),
        width: optionNavVisibile ? 'calc(100% - 18vw)' : 'calc(100% - 9vw)',
    };

    useEffect(() => {
        if (hideReportNavMenu) {
            setOptionNavVisibile(false);
        }

        setSelectedReport({});
        setLoadError(null);
        setReportsLoading(true);

        const fetchData = async () => {
            const abortController = new AbortController();
            const endpoint = trakrefReports
                ? Routes.PowerBI.GetTrakrefReports()
                : Routes.PowerBI.GetReports()
            const response = await fetch(endpoint, {
                signal: abortController.signal,
            });

            // AB#17059 / FEXA199: the report list is role-gated now - surface a 403 instead of hanging
            // the loading bar on the empty body (the Location Audit toast links any user here).
            if (!response.ok) {
                setLoadError(response.status === 403
                    ? 'You do not have permission to view these reports.'
                    : 'These reports could not be loaded. Please try again.');
                setAllReports([]);
                setSavedReports([]);
                setLoading(false);
                setReportsLoading(false);
                return;
            }

            const data = await response.json();

            // AB#17144: an account without a Power BI workspace has no BI Dashboard reports.
            if (!trakrefReports && !data?.reports?.length) {
                setLoadError('No BI Dashboard reports are set up for this account.');
                setAllReports([]);
                setSavedReports([]);
                setLoading(false);
                setReportsLoading(false);
                return;
            }

            const formattedTrakrefReportObject = {
                title: trakrefReports ? "Reports" : "BI Dashboard",
                search: true,
                render: true,
                isPowerBI: true,
                reports: [...data.reports],
            };

            const allReportsArray = [formattedTrakrefReportObject];

            if (!trakrefReports) {
                setReportListExpanded(0);
            }

            setAllReports(allReportsArray);
            setSavedReports(allReportsArray);

            setLoading(false);
            setReportsLoading(false);

            if (reportName && reportName != "") {

                var report = allReportsArray[0].reports.find(cr => cr.name === reportName);

                if (report) {
                    const selectedReport = {
                        reportType: report.type,
                        id: report.id,
                        embedUrl: report.embedUrl,
                        accessToken: report.token,
                        isManualReport: allReportsArray[0].isManualReport,
                        isPowerBI: allReportsArray[0].isPowerBI,
                        name: report.name,
                        key: uuid.getUuid()
                    };

                    const newBreadCrumbs = [
                        { text: title },
                        { text: selectedReport.name }
                    ];

                    setBreadCrumbs(newBreadCrumbs);
                    setSelectedReport(selectedReport);
                }
            }

        };

        const determineBreadCrumbs = () => {
            const newBreadCrumbs = [{ text: title }];
            setBreadCrumbs(newBreadCrumbs);
        };

        fetchData();
        determineBreadCrumbs();
    }, [trakrefReports]);

    const expandReportList = (reportIndex) => setReportListExpanded(reportIndex === reportListExpanded ? null : reportIndex);

    const selectReport = (report_type, report_id) => {

        const parent = allReports.find(r => r.type === report_type);
        var childReport = parent.reports.find(cr => cr.id === report_id);

        const selectedReport = {
            reportType: childReport.type,
            id: report_id,
            embedUrl: childReport.embedUrl,
            accessToken: childReport.token,
            isManualReport: parent.isManualReport,
            isPowerBI: parent.isPowerBI,
            name: childReport.name,
            key: uuid.getUuid()
        };

        const newBreadCrumbs = [
            { text: title },
            { text: selectedReport.name }
        ];

        setBreadCrumbs(newBreadCrumbs);
        setSelectedReport(selectedReport);
    };


    const determineSelectedReport = selected_report_id => selectedReport.id === selected_report_id;

    const handleSearch = (searchTerm) => {
        setFilterSearch(searchTerm);
        const [reportListToFilter, ...rest] = savedReports;
        let reportClone = { ...reportListToFilter };
        const filteredReports = reportClone.reports.filter(report => report.name.toLowerCase().includes(searchTerm.toLowerCase()));
        reportClone.reports = filteredReports;
        const allReportsFiltered = [reportClone, ...rest];
        return setAllReports(allReportsFiltered);
    };

    if (loading) {
        return <LoadingBar />;
    }

    if (loadError) {
        return <Box sx={{ padding: '24px' }}>{loadError}</Box>;
    }

    return (
        <>
            <div style={{
                position: 'relative',
                left: optionNavVisibile ? '11.5vw' : '-2%',
                top: '-3vh',
                maxWidth: '10%',
            }}>
                <Tooltip hidden={hideReportNavMenu} title={optionNavVisibile ? "Collapse List" : "Open List"}>
                    <IconButton
                        onClick={() => setOptionNavVisibile(visible => !visible)}
                    >
                        <KeyboardTabIcon
                            size={22}
                            sx={{
                                transform: optionNavVisibile ? "scaleX(-1)" : "scaleX(1)",
                                '& .MuiButtonBase-root': { outline: 'none', padding: 0 }
                            }}
                        />
                    </IconButton>
                </Tooltip>
            </div>
            <Drawer
                hideBackdrop
                variant='persistent'
                anchor="left"
                elevation={0}
                open={optionNavVisibile}
                onClose={() => setOptionNavVisibile(visible => !visible)}
                sx={{
                    width: '13vw',
                    [`& .MuiDrawer-paper`]: {
                        width: '13vw',
                        height: reportOptionsContainerHeight,
                        boxSizing: 'border-box',
                        position: "absolute"
                    },
                }}
            >
                <ReportOptions
                    allReports={allReports}
                    reportListExpanded={reportListExpanded}
                    expandReportList={expandReportList}
                    selectReport={selectReport}
                    searchIsOn={searchIsOn}
                    setSearchIsOn={setSearchIsOn}
                    filterSearch={filterSearch}
                    handleSearch={handleSearch}
                    determineSelectedReport={determineSelectedReport}
                    selectedReport={selectedReport}
                    reportsLoading={reportsLoading}
                />
            </Drawer>

            <Box component="main" sx={childrenStyle} marginTop={hideReportNavMenu ? "22px" : 0}>
                <ReportContainer
                    trakrefReports={trakrefReports}
                    selectReportName={selectedReport.name}
                    breadCrumbs={breadCrumbs}
                    rls={rls}
                    selectedReport={selectedReport}
                />
            </Box>
        </>
    );
};

export default PowerBI;
